import React, { useEffect } from "react";
import { FormControl, InputLabel, TextField, Box, makeStyles, InputAdornment, Icon } from "@material-ui/core";
import { HelpIcon, HelpProps } from "./HelpIcon";
import { phonesFormatter } from "../helpers/inputs/phones/phonesFormatter";
import clsx from "clsx";

export type TextInputProps = {
  label?: {
    id?: string;
    title?: string;
  };
  help?: HelpProps;
  input?: {
    value?: string;
    onChange?: Function;
    placeholderText?: string;
    helperText?: string;
    hasError?: boolean;
    id?: string;
    inputClass?: string;
    name?: string;
    autoComplete?: string;
  };
  forwardedRef?: React.RefObject<HTMLDivElement>;
};

const useStyles = makeStyles(() => ({
  box: {
    display: "flex",
    alignItems: "center",
  },
}));

export const TextInputGroupBasePhone: React.FC<TextInputProps> = (props: TextInputProps) => {
  const css = useStyles();

  const labelElement = props.label?.title && (
    <InputLabel id={props.label?.id} shrink={false}>
      {props.label?.title}
    </InputLabel>
  );

  const textInput = React.createRef<HTMLInputElement>();
  const phoneFormatter = phonesFormatter["sv"];

  const help = props.help;
  const helpElement = help && (help.title || help.description) ? <HelpIcon title={help.title} description={help.description} /> : null;

  let labelArea;
  if (labelElement) {
    labelArea = (
      <Box className={css.box}>
        {labelElement}
        {helpElement}
      </Box>
    );
  }

  const value = props.input?.value;

  useEffect(() => {
    if (!textInput?.current) {
      return;
    }
    const { inputFormatted } = phoneFormatter.formatInput(props.input?.value || "", 0, phoneFormatter.formatMask);
    textInput.current.value = inputFormatted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const hasError = props.input?.hasError;

  useEffect(() => {
    const timer = setTimeout(() => {
      const input = document.querySelector(".Mui-error>input") as HTMLInputElement;
      input?.focus();
    }, 1);
    return () => clearTimeout(timer);
  }, [hasError]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    phoneFormatter.keyFilter(event, phoneFormatter.formatMask, phoneFormatter.countryPrefix);
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    const { inputFormatted, cursorPosition } = phoneFormatter.formatInput(
      target.value,
      target.selectionStart || 0,
      phoneFormatter.formatMask,
    );
    target.value = inputFormatted;
    target.selectionStart && target.setSelectionRange(cursorPosition as number, cursorPosition as number);
  };

  const onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    if (target.value.length < phoneFormatter.countryPrefix.length) {
      target.value = phoneFormatter.countryPrefix;
    }
  };

  function OnBlur(event: React.FocusEvent<HTMLInputElement>) {
    const currentValue = props.input?.value;
    const newValue = event.target.value.replaceAll(" ", "");
    const changeEvent = props.input?.onChange;
    currentValue !== newValue && changeEvent && changeEvent(newValue);
  }

  return (
    <Box marginY={1} id={"ws-input-" + props.input?.id}>
      <FormControl fullWidth>
        {labelArea}
        <TextField
          className={clsx(props?.input?.inputClass)}
          id={props.input?.id}
          name={props.input?.name}
          autoComplete={props.input?.autoComplete}
          placeholder={props.input?.placeholderText}
          variant='outlined'
          helperText={props.input?.helperText}
          error={props.input?.hasError}
          inputRef={props.forwardedRef}
          onBlur={OnBlur} // update state in store on focus lost
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Icon>{phoneFormatter.flagIcon}</Icon>
              </InputAdornment>
            ),
          }}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onChange={onChange}
        />
      </FormControl>
    </Box>
  );
};
